import Utils from './utils/utils';

type Component = {
  component: string;
  scripts?: string[];
  styles?: string[];
  [key: string]: any;
};

type ComponentMap = { [key: string]: Component };

class ComponentsRegistry {
  public customComponentLazyMap: ComponentMap;
  public componentMap: ComponentMap;
  public overrideMap: ComponentMap;
  public lazyMap: ComponentMap;

  constructor() {
    this.componentMap = {};
    this.overrideMap = {};
    this.lazyMap = {};
    this.customComponentLazyMap = {};
  }

  /**
   * Registers the component with core so that renderer can
   * get the right component while rendering.
   *
   * @param componentName Name of the component eq., Currency
   * @param component component object example
   * {
   *  component: "Currency",
   *  scripts: ["components/Currency/index.js"]
   * }
   * @example <caption>PCore.getComponentsRegistry().register(componentName, component)</caption>
   * PCore.getComponentsRegistry().register("Currency", {
   *  component: "Currency",
   *  scripts: ["components/Currency/index.js"]
   * });
   *
   * @memberof ComponentsRegistry
   */
  register(componentName: string, component: Component) {
    this.componentMap[componentName] = component;
  }

  /**
   * Registers the component with core so that renderer can
   * get the right component while rendering.
   *
   * @param componentName Name of the component eq., Currency
   * @param component component object example
   * {
   *  component: "Currency",
   *  scripts: ["components/Currency/index.js"]
   * }
   * @example <caption>PCore.getComponentsRegistry().register(componentName, component)</caption>
   * PCore.getComponentsRegistry().register("Currency", {
   *  component: "Currency",
   *  scripts: ["components/Currency/index.js"]
   * });
   *
   * @memberof ComponentsRegistry
   */
  registerLazyMap(componentName: string, component: Component) {
    this.lazyMap[componentName] = component;
  }

  /**
   * Registers the component with core so that renderer can
   * get the right component while rendering.
   *
   * @param componentName Name of the component eq., Currency
   * @param component component object example
   * {
   *  component: "Currency",
   *  scripts: ["components/Currency/index.js"]
   * }
   * @example <caption>PCore.getComponentsRegistry().register(componentName, component)</caption>
   * PCore.getComponentsRegistry().register("Currency", {
   *  component: "Currency",
   *  scripts: ["components/Currency/index.js"]
   * });
   *
   * @memberof ComponentsRegistry
   */
  registerCustomComponentLazyMap(componentName: string, component: Component) {
    this.customComponentLazyMap[componentName] = component;
  }

  /**
   * Gets the component object registered with core.
   *
   * @param componentName Name of the component eq., Currency
   * @example <caption>PCore.getComponentsRegistry().getComponent(componentName)</caption>
   * const component = PCore.getComponentsRegistry().getComponent("Currency");
   * @returns component object example
   * {
   *  component: "Currency",
   *  scripts: ["components/Currency/index.js"]
   * }
   * @memberof ComponentsRegistry
   */
  getComponent(componentName: string): Component | undefined {
    return this.componentMap[componentName];
  }

  /**
   * Gets the component object registered with core.
   *
   * @param componentName Name of the component eq., Currency
   * @example <caption>PCore.getComponentsRegistry().getComponent(componentName)</caption>
   * const component = PCore.getComponentsRegistry().getComponent("Currency");
   * @returns component object example
   * {
   *  component: "Currency",
   *  scripts: ["components/Currency/index.js"]
   * }
   * @memberof ComponentsRegistry
   */
  getLazyComponent(componentName: string): Component | undefined {
    return this.lazyMap[componentName];
  }

  /**
   * Gets the component object registered with core.
   *
   * @param componentName Name of the component eq., Currency
   * @example <caption>PCore.getComponentsRegistry().getComponent(componentName)</caption>
   * const component = PCore.getComponentsRegistry().getComponent("Currency");
   * @returns component object example
   * {
   *  component: "Currency",
   *  scripts: ["components/Currency/index.js"]
   * }
   * @memberof ComponentsRegistry
   */
  getCustomComponent(componentName: string): Component | undefined {
    return this.customComponentLazyMap[componentName];
  }

  /**
   * Merges the componentMap to registry.
   *
   * @example <caption>PCore.getComponentsRegistry().mergeComponentsMap(componentsMap)</caption>
   * const component = PCore.getComponentsRegistry().mergeComponentsMap(reactComponentsMap);
   * @param componentsMap object which holds component definitions eq.,
   * const reactComponentsMap = {
   * Renderer: {
   * renderer: "PegaReactRenderer",
   * scripts: ["renderer/react_renderer.js"]
   * },
   * Currency: {
   * component: "Currency",
   * scripts: ["components/Currency/index.js"]
   * }
   * }
   * @memberof ComponentsRegistry
   */
  mergeComponentsMap(componentsMap: ComponentMap = {}) {
    Utils.copy(componentsMap, this.componentMap);
  }

  /**
   * Merges override map
   * @param overrideMap object that holds override map.
   * @memberof ComponentsRegistry
   */
  mergeOverrideMap(overrideMap: ComponentMap = {}) {
    Utils.copy(overrideMap, this.overrideMap);
  }

  /**
   * returns override map
   * @memberof ComponentsRegistry
   */
  getOverrideMap() {
    return this.overrideMap;
  }

  /**
   * returns Lazy map
   * @memberof ComponentsRegistry
   */
  getLazyMap() {
    return this.lazyMap;
  }
}

export default new ComponentsRegistry();
